import { useEffect, useState } from 'react';
import localVoiceBots from './voice_bots.json';

export interface VoiceBotSample {
  label: string;
  url: string;
}

export interface VoiceBot {
  key: string;
  name: string;
  description: string;
  category: string;
  language: string;
  language_accent?: string;
  flag: string;
  voice_backend: string;
  public: boolean;
  samples: VoiceBotSample[];
  persona_name?: string;
  gender?: 'male' | 'female';
  role?: string;
  default_voice?: string;
  entity_name?: string;
}

export interface VoiceBotsResponse {
  bots: VoiceBot[];
}

export const CUSTOM_BOT_KEY = 'custom';

/** Bots pinned to the top of the public telephony grid, in display order. */
const PUBLIC_BOT_PRIORITY = ['raisy', 'biz_edu_playano', 'biz_sales_flixn', 'biz_video_glowtest'];

export const getBotByKey = (bots: VoiceBot[], key: string): VoiceBot | undefined =>
  bots.find((b) => b.key === key);

export const getPublicBots = (bots: VoiceBot[]): VoiceBot[] => {
  const visible = bots.filter((b) => b.public !== false);
  return visible.sort((a, b) => {
    const pa = PUBLIC_BOT_PRIORITY.indexOf(a.key);
    const pb = PUBLIC_BOT_PRIORITY.indexOf(b.key);
    if (pa !== -1 && pb !== -1) return pa - pb;
    if (pa !== -1) return -1;
    if (pb !== -1) return 1;
    return 0;
  });
};

/**
 * Resolve a bot portrait URL by key and size.
 *
 * Place generated WebP assets in the public folder, e.g.:
 *   public/assets/bots/{key}_512.webp
 * The path returned is root-relative so it works on both dev and production builds.
 */
export const getBotImageUrl = (key: string, size: 128 | 256 | 512 | 1024 = 512): string =>
  `/assets/bots/${key}_${size}.webp`;

const VOICE_API_URL = import.meta.env.VITE_VOICE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface UseVoiceBotsResult {
  bots: VoiceBot[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch the centralized voice-bot index from the voice backend.
 *
 * The JSON single source of truth lives in raisy_com/src/config/voice_bots.json
 * and is served by GET /api/v1/telephony/bots.
 *
 * UX priority: if the backend is unavailable, we immediately fall back to the
 * local copy bundled with the frontend so the page never appears empty.
 */
export function useVoiceBots(): UseVoiceBotsResult {
  const [bots, setBots] = useState<VoiceBot[]>(localVoiceBots.bots || []);
  const [loading, setLoading] = useState<boolean>((localVoiceBots.bots?.length ?? 0) === 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`${VOICE_API_URL}/api/v1/telephony/bots`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: VoiceBotsResponse = await res.json();
        if (!cancelled) {
          let mergedBots: VoiceBot[] = Array.isArray(data.bots) ? data.bots : localVoiceBots.bots || [];
          const localByKey = new Map((localVoiceBots.bots || []).map((b) => [b.key, b]));
          mergedBots = mergedBots.map((bot) => {
            const local = localByKey.get(bot.key);
            if (!local) return bot;
            return {
              ...bot,
              persona_name: bot.persona_name || local.persona_name,
              gender: bot.gender || local.gender,
              role: bot.role || local.role,
              default_voice: bot.default_voice || local.default_voice,
              entity_name: bot.entity_name || local.entity_name,
              language_accent: bot.language_accent || local.language_accent,
            };
          });
          setBots(mergedBots);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // Backend unavailable — keep the locally bundled bots, no user-visible error.
          setBots(localVoiceBots.bots || []);
          setError(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { bots, loading, error };
}
